import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${""}/api`;

const AuthGate = ({ children, onUserChange, initialView = 'login' }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState(initialView);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);  // seconds remaining on resend lockout
  const [resendMsg, setResendMsg] = useState('');           // inline "Code resent" confirmation

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    full_name: '', email: '', password: '', confirm_password: '',
    role: 'practitioner', practice_name: '', npi_number: '', specialty: '', credentials: ''
  });
  const [resetForm, setResetForm] = useState({ email: '', token: '', new_password: '' });
  const [selectedRole, setSelectedRole] = useState(''); // E1: Registration role selection

  // BUG #1 fix (2026-06-23) — Parse reset_token from URL on mount.
  // Email reset links land at /login?reset_token=ABCD1234. Previously AuthGate
  // ignored this param entirely, leaving users on the login form with no idea
  // they came from a reset email. Now we auto-switch to the reset view with
  // the token pre-filled.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('reset_token');
      if (urlToken) {
        setResetToken(urlToken);
        setResetForm(prev => ({ ...prev, token: urlToken }));
        setResetSent(true);  // show the reset form, not the request-reset form
        setView('forgot');
      }
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('pp_auth');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.token && new Date() < new Date(data.expiry)) {
          axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${data.token}` } })
            .then(res => {
              if (res.data.success) {
                setIsAuthenticated(true);
                onUserChange?.(res.data.user);
              } else { localStorage.removeItem('pp_auth'); }
            })
            .catch(() => { localStorage.removeItem('pp_auth'); })
            .finally(() => {
              // If pp_auth verification failed, fall back to beta_access check
              if (!localStorage.getItem('pp_auth')) {
                const path = typeof window !== 'undefined' ? (window.location.pathname || '') : '';
                const isAuthRoute = ['/signup', '/login', '/register', '/forgot-password', '/reset-password'].some(p => path === p || path.startsWith(p + '/'));
                const legacy = localStorage.getItem('beta_access');
                if (legacy && !isAuthRoute) {
                  try {
                    const data = JSON.parse(legacy);
                    if (data.granted && new Date() < new Date(data.expiry)) {
                      setIsAuthenticated(true);
                      onUserChange?.({ full_name: data.userName || 'Practitioner', role: 'practitioner', subscription_tier: 'free_trial' });
                      setIsLoading(false);
                      return;
                    }
                  } catch {}
                }
              }
              setIsLoading(false);
            });
          return;
        }
        localStorage.removeItem('pp_auth');
      } catch { localStorage.removeItem('pp_auth'); }
    }
    // Check legacy beta access — but NEVER when the user explicitly navigated to an
    // auth route (/signup, /login, /register, /forgot-password). Otherwise anyone
    // who tried "Continue Without Account" first is silently bounced into the app
    // instead of seeing the signup form. (2026-04-23 hotfix Bug 1)
    const path = typeof window !== 'undefined' ? (window.location.pathname || '') : '';
    const isAuthRoute = ['/signup', '/login', '/register', '/forgot-password', '/reset-password'].some(p => path === p || path.startsWith(p + '/'));
    const legacy = localStorage.getItem('beta_access');
    if (legacy && !isAuthRoute) {
      try {
        const data = JSON.parse(legacy);
        if (data.granted && new Date() < new Date(data.expiry)) {
          setIsAuthenticated(true);
          onUserChange?.({ full_name: data.userName || 'Practitioner', role: 'practitioner', subscription_tier: 'free_trial' });
          setIsLoading(false);
          return;
        }
      } catch {}
    }
    setIsLoading(false);
  }, []);

  const saveAuth = (token, user) => {
    // BUG #3 fix (2026-06-23) — Token expiry raised from 3 days to 30 days.
    // 3-day expiry caused 70+ users to be locked out after one signup session
    // (per onboarding_root_causes_jun21 analysis). 30 days matches typical early-
    // adoption SaaS sessions and gives users time to return without re-auth pain.
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30);
    localStorage.setItem('pp_auth', JSON.stringify({ token, expiry: expiry.toISOString() }));
    // Clear the legacy beta_access token on any successful real login/signup so we
    // never bounce this user back into beta mode. (2026-04-23 hotfix Bug 1)
    try { localStorage.removeItem('beta_access'); } catch {}
    setIsAuthenticated(true);
    onUserChange?.(user);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      if (res.data.success) saveAuth(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally { setSubmitting(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (signupForm.password !== signupForm.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (signupForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/register`, signupForm);
      if (res.data.success) {
        localStorage.setItem('pp_show_welcome', 'true');
        saveAuth(res.data.token, res.data.user);

        // D1: Apply referral code if present in URL
        const urlParams = new URLSearchParams(window.location.search);
        const refCode = urlParams.get('ref');
        if (refCode) {
          try {
            await axios.post(`${API}/referral/apply`, { code: refCode, email: signupForm.email });
          } catch (refErr) {
            console.warn('Referral code apply failed:', refErr);
          }
        }

        // B3: If contributing role, also submit application
        if (signupForm.role === 'contributing') {
          try {
            await axios.post(`${API}/contributing/apply`, {
              email: signupForm.email,
              full_name: signupForm.full_name,
              practice_name: signupForm.practice_name,
              specialty: signupForm.specialty,
              experience: signupForm.experience || '',
              motivation: signupForm.motivation || '',
            });
          } catch (applyErr) {
            console.warn('Contributing application submit failed:', applyErr);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: resetForm.email });
      if (res.data.success) {
        setResetSent(true);
        if (res.data.reset_token) setResetToken(res.data.reset_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Request failed');
    } finally { setSubmitting(false); }
  };

  // Tick down the resend cooldown once per second; clean up on unmount or when it hits 0.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Auto-clear the inline "Code resent" confirmation after 3s.
  useEffect(() => {
    if (!resendMsg) return;
    const id = setTimeout(() => setResendMsg(''), 3000);
    return () => clearTimeout(id);
  }, [resendMsg]);

  const handleResendCode = async () => {
    if (resendCooldown > 0 || submitting) return;
    setError('');
    setResendCooldown(60);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: resetForm.email });
      if (res.data.reset_token) setResetToken(res.data.reset_token);
      setResendMsg('Code resent — check your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend code. Please try again.');
      setResendCooldown(0);  // let them retry immediately on failure
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (resetForm.new_password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token: resetForm.token || resetToken, new_password: resetForm.new_password });
      if (res.data.success) { setView('login'); setError(''); setResetSent(false); }
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed');
    } finally { setSubmitting(false); }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="animate-pulse text-teal-600 text-lg font-medium">Loading PeptideProtocols.ai...</div>
      </div>
    );
  }

  if (isAuthenticated) return <>{children}</>;

  const inputCls = "w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all";
  const btnCls = "w-full bg-teal-600 hover:bg-teal-700 text-white py-3 px-4 rounded-xl font-semibold transition-all disabled:opacity-50";
  const linkCls = "text-teal-600 hover:text-teal-800 text-sm font-medium cursor-pointer";

  // Login View
  if (view === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-md w-full">
          <div className="mb-4">
            <a href="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-600" data-testid="login-back-home">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
            </a>
          </div>
          <div className="text-center mb-8">
            <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide - AI clinical agent for peptide protocols" className="h-20 w-20 mx-auto rounded-full border-2 border-teal-400 shadow-lg mb-4" />
            <h1 className="text-4xl font-bold text-slate-800 mb-2">PeptideProtocols.ai</h1>
            <p className="text-teal-600">Clinical-Grade Peptide Therapy Protocols</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-bold text-slate-800 mb-1 text-center">Sign In</h2>
            {/* 2026-06-23 audit: subtitle was role-specific ("practitioner"), alienating
                contributing MDs, ambassadors, and admins who share this same login. */}
            <p className="text-slate-500 text-sm mb-6 text-center">One sign in for practitioners, contributors, and ambassadors</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input type="email" value={loginForm.email} onChange={e => setLoginForm({...loginForm, email: e.target.value})}
                  className={inputCls} placeholder="doctor@practice.com" required data-testid="login-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={loginForm.password}
                    onChange={e => setLoginForm({...loginForm, password: e.target.value})}
                    className={inputCls + " pr-10"} placeholder="Enter your password" required data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm" data-testid="auth-error">{error}</div>}
              <button type="submit" disabled={submitting} className={btnCls} data-testid="login-submit-btn">
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <div className="mt-4 flex justify-between items-center">
              <button onClick={() => { setView('forgot'); setError(''); }} className={linkCls} data-testid="forgot-password-link">Forgot password?</button>
              <button onClick={() => { setView('signup'); setError(''); }} className={linkCls} data-testid="signup-link">Create account</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Signup View — Registration
  if (view === 'signup') {
    const roleOptions = [
      { id: 'practitioner', label: 'Pre-Release Access', desc: 'Full platform access with 50% off when billing activates', icon: '1' },
      { id: 'contributing', label: 'Contributing Practitioner', desc: 'Lifetime free access — shape the AI with your expertise', icon: '2' },
    ];

    if (!selectedRole) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-lg w-full">
            <div className="text-center mb-6">
              <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide" className="h-12 w-12 mx-auto rounded-full border-2 border-teal-400 mb-3" />
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">Join PeptideProtocols.ai</h1>
              <p className="text-teal-600 text-sm">Choose how you'd like to get started</p>
            </div>
            <div className="space-y-3" data-testid="role-selection">
              {roleOptions.map(opt => (
                <button key={opt.id} onClick={() => { setSelectedRole(opt.id); setSignupForm(f => ({...f, role: opt.id})); }}
                  className="w-full text-left p-4 bg-white rounded-xl border-2 border-slate-200 hover:border-teal-400 hover:shadow-md transition-all group"
                  style={{ minHeight: '44px' }}
                  data-testid={`role-${opt.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-700 font-bold text-sm flex items-center justify-center flex-shrink-0 group-hover:bg-teal-100">{opt.icon}</div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{opt.label}</p>
                      <p className="text-xs text-slate-500">{opt.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 text-center">
              <button onClick={() => { setView('login'); setError(''); }} className="flex items-center gap-1 mx-auto text-sm text-slate-500 hover:text-teal-600">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </div>
          </div>
        </div>
      );
    }

    const roleLabels = { practitioner: 'Pre-Release Access', contributing: 'Contributing Practitioner' };
    const ctaLabel = selectedRole === 'contributing' ? 'Submit Application' : 'Create Account';

    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-lg w-full">
          <div className="text-center mb-6">
            <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide" className="h-12 w-12 mx-auto rounded-full border-2 border-teal-400 mb-3" />
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">Create Account</h1>
            <p className="text-teal-600 text-sm">{roleLabels[selectedRole] || 'Registration'}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                  <input type="text" value={signupForm.full_name} onChange={e => setSignupForm({...signupForm, full_name: e.target.value})}
                    className={inputCls} placeholder="Dr. Jane Smith" required data-testid="signup-name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input type="email" value={signupForm.email} onChange={e => setSignupForm({...signupForm, email: e.target.value})}
                    className={inputCls} placeholder="doctor@practice.com" required data-testid="signup-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                  <input type="password" value={signupForm.password} onChange={e => setSignupForm({...signupForm, password: e.target.value})}
                    className={inputCls} placeholder="Min 8 characters" required data-testid="signup-password" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password *</label>
                  <input type="password" value={signupForm.confirm_password} onChange={e => setSignupForm({...signupForm, confirm_password: e.target.value})}
                    className={inputCls} placeholder="Confirm password" required data-testid="signup-confirm-password" />
                </div>
              </div>
              {/* Contributing-specific fields */}
              {selectedRole === 'contributing' && (
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wider">Contributing Details</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Years of Peptide Experience</label>
                      <input type="text" value={signupForm.experience || ''} onChange={e => setSignupForm({...signupForm, experience: e.target.value})}
                        className={inputCls} placeholder="e.g., 5 years" data-testid="signup-experience" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Why do you want to contribute?</label>
                      <textarea value={signupForm.motivation || ''} onChange={e => setSignupForm({...signupForm, motivation: e.target.value})}
                        className={inputCls + " resize-none"} rows={3} placeholder="Tell us about your interest..." data-testid="signup-motivation" />
                    </div>
                  </div>
                </div>
              )}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wider">Practice Details (Optional)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Practice Name</label>
                    <input type="text" value={signupForm.practice_name} onChange={e => setSignupForm({...signupForm, practice_name: e.target.value})}
                      className={inputCls} placeholder="Wellness Clinic" data-testid="signup-practice" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">NPI Number</label>
                    <input type="text" value={signupForm.npi_number} onChange={e => setSignupForm({...signupForm, npi_number: e.target.value})}
                      className={inputCls} placeholder="1234567890" data-testid="signup-npi" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Specialty</label>
                    <select value={signupForm.specialty} onChange={e => setSignupForm({...signupForm, specialty: e.target.value})}
                      className={inputCls} data-testid="signup-specialty">
                      <option value="">Select specialty</option>
                      <option value="Anti-Aging Medicine">Anti-Aging Medicine</option>
                      <option value="Cardiology">Cardiology</option>
                      <option value="Dermatology">Dermatology</option>
                      <option value="Endocrinology">Endocrinology</option>
                      <option value="Functional Medicine">Functional Medicine</option>
                      <option value="Gastroenterology">Gastroenterology</option>
                      <option value="Integrative Medicine">Integrative Medicine</option>
                      <option value="Internal Medicine">Internal Medicine</option>
                      <option value="Naturopathic Medicine">Naturopathic Medicine</option>
                      <option value="Pain Management">Pain Management</option>
                      <option value="Pediatrics">Pediatrics</option>
                      <option value="Psychiatry">Psychiatry</option>
                      <option value="Sports Medicine">Sports Medicine</option>
                      <option value="Women's Health">Women's Health</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Credentials</label>
                    <select value={signupForm.credentials} onChange={e => setSignupForm({...signupForm, credentials: e.target.value})}
                      className={inputCls} data-testid="signup-credentials">
                      <option value="">Select credentials</option>
                      <option value="APRN">APRN</option>
                      <option value="DC">DC</option>
                      <option value="DO">DO</option>
                      <option value="DPT">DPT</option>
                      <option value="LAc">LAc (Licensed Acupuncturist)</option>
                      <option value="MD">MD</option>
                      <option value="ND">ND</option>
                      <option value="NP">NP</option>
                      <option value="PA">PA</option>
                      <option value="PharmD">PharmD</option>
                      <option value="PhD">PhD</option>
                      <option value="RD">RD</option>
                      <option value="RN">RN</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm" data-testid="auth-error">{error}</div>}
              <button type="submit" disabled={submitting} className={btnCls} data-testid="signup-submit-btn">
                {submitting ? 'Creating account...' : ctaLabel}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button onClick={() => setSelectedRole('')} className="flex items-center gap-1 mx-auto text-sm text-slate-500 hover:text-teal-600">
                <ArrowLeft className="h-3.5 w-3.5" /> Choose a different path
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password View
  if (view === 'forgot') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Reset Password</h1>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {!resetSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-slate-500 mb-2">Enter your email and we'll send you a reset code.</p>
                <input type="email" value={resetForm.email} onChange={e => setResetForm({...resetForm, email: e.target.value})}
                  className={inputCls} placeholder="doctor@practice.com" required data-testid="reset-email" />
                {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm">{error}</div>}
                <button type="submit" disabled={submitting} className={btnCls} data-testid="reset-submit-btn">
                  {submitting ? 'Sending...' : 'Send Reset Code'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <p className="text-sm text-teal-600 mb-2">Check your email — we sent a reset code to {resetForm.email || 'your email'}. It may take up to a minute to arrive. Enter the code below with your new password.</p>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reset Code</label>
                  <input type="text" value={resetForm.token || resetToken} onChange={e => setResetForm({...resetForm, token: e.target.value})}
                    className={inputCls + " text-center tracking-widest font-mono"} placeholder="Enter code from email" required data-testid="reset-token" />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-teal-600" data-testid="resend-confirmation">{resendMsg}</span>
                    <button type="button" onClick={handleResendCode} disabled={resendCooldown > 0}
                      className="text-teal-600 hover:text-teal-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="resend-code-btn">
                      {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                  <input type="password" value={resetForm.new_password} onChange={e => setResetForm({...resetForm, new_password: e.target.value})}
                    className={inputCls} placeholder="Min 8 characters" required data-testid="reset-new-password" />
                </div>
                {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm">{error}</div>}
                <button type="submit" disabled={submitting} className={btnCls} data-testid="reset-password-btn">
                  {submitting ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}
            <div className="mt-4 text-center">
              <button onClick={() => { setView('login'); setError(''); setResetSent(false); }} className="flex items-center gap-1 mx-auto text-sm text-slate-500 hover:text-teal-600">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthGate;
